import type { ListedWorkbook, WorkbookListParams } from '@mathflat/domain/Workbook'
import { omitBy } from 'lodash-es'
import { SchoolGradeSemesterSubjectFilter } from 'src/dto'
import type { SchoolGradeSemesterSubjectFilterData } from 'src/dto/common/SchoolGradeSemesterSubjectFilter'
import type { WorkbookFilterParamT } from 'src/page/Lesson-prepare/LessonMaterial/lessonMaterial'

import { curriculumCoreService } from '@/service/curriculumCore.service'

export class WorkbookFilter<
  T extends ListedWorkbook['type'] = ListedWorkbook['type'],
  U extends 'GRADE' | 'CLASS' | 'STUDENT' | never = never,
> extends SchoolGradeSemesterSubjectFilter<WorkbookFilterParamT<T, U>> {
  constructor(data?: SchoolGradeSemesterSubjectFilterData<WorkbookFilterParamT<T, U>>) {
    super(data)
  }

  override set(
    data: Parameters<SchoolGradeSemesterSubjectFilter<WorkbookFilterParamT<T, U>>['set']>[number],
  ) {
    const workbookType = data.type ?? this.params.type
    switch (workbookType) {
      case 'CUSTOM_SIGNATURE':
      case 'ACADEMY_GROUP':
        this.elementaryDropdownList = curriculumCoreService.getGradeSemesterElementaryFrom3rdGrade()
        this.middleDropdownList = curriculumCoreService.getGradeSemesterMiddle()
        // 1차오픈: 시그니처교재 항목 추가됨
        // 이전코드) { includePreOpen: false, }
        this.highDropdownList = curriculumCoreService.getGradeSubjectHigh({ includePreOpen: true })
        break
      case 'PUBLIC':
        this.elementaryDropdownList = curriculumCoreService
          .getGradeSemesterElementary()
          .filter(
            ({ gradeSubject, revision }) =>
              !((gradeSubject === '1' || gradeSubject === '2') && revision === 'CURRICULUM_15'),
          )
        this.middleDropdownList = curriculumCoreService.getGradeSemesterMiddle()
        this.highDropdownList = curriculumCoreService.getGradeSubjectHigh()
        break
      case 'SCHOOL':
        this.elementaryDropdownList = curriculumCoreService
          .getGradeSemesterElementary()
          .filter(
            ({ gradeSubject, revision }) =>
              !((gradeSubject === '1' || gradeSubject === '2') && revision === 'CURRICULUM_15'),
          )
        this.middleDropdownList = curriculumCoreService.getGradeMiddle()
        this.highDropdownList = curriculumCoreService.getGradeSubjectHigh()
        break
      case 'CUSTOM':
        this.elementaryDropdownList = curriculumCoreService.getGradeSemesterElementary()
        this.middleDropdownList = curriculumCoreService.getGradeSemesterMiddle()
        this.highDropdownList = curriculumCoreService.getGradeSubjectHigh()
        break
    }

    if (this.params.schoolType === 'MIDDLE') {
      if (
        workbookType === 'PUBLIC' ||
        workbookType === 'CUSTOM' ||
        workbookType === 'CUSTOM_SIGNATURE' ||
        workbookType === 'ACADEMY_GROUP'
      ) {
        if (this.params.type === 'SCHOOL' && this.selectedTrieKey !== 'ALL') {
          // 다른교재와 교과서는 함께 관리가 되는데 중학교 교과서는 학년기반이므로 semester가 없음.
          // 따라서 교과서 -> 다른교재로 올때는 lowerTrieKeys[0](1학기) 사용 (학기까지 set 해주어야 하므로)
          const _item = curriculumCoreService.getItemByTrieKey(this.selectedTrieKey)
          super.set({
            trieKey: _item?.lowerTrieKeys[0],
            ...data,
          })
        } else {
          super.set(data)
        }
      } else {
        // 교과서
        if (this.params.type !== 'SCHOOL' && this.selectedTrieKey !== 'ALL') {
          // 다른교재 -> 교과서로 올때는 학년으로 set 해주어야 하므로 upperTrieKey 사용
          const _item = curriculumCoreService.getItemByTrieKey(this.selectedTrieKey)
          super.set({
            trieKey: _item?.upperTrieKey,
            ...data,
          })
        } else {
          super.set(data)
        }
      }
      return
    }

    super.set(data)
  }

  getValidParams() {
    // isLast 는 더 불러올지 정하는 화면 상태라 요청에 싣지 않는다
    const { isLast: _isLast, type, ...params } = this.params
    const validParams = omitBy(params, (v, k) => {
      return (
        v === '' ||
        v === 'ALL' ||
        // SCHOOL 일때 publicWorkbookAnswerConfirmed 자체가 없어야하는데 params가 시중교재와 묶여있어서 분리하기 전까진 예외처리
        (type === 'SCHOOL' && k === 'publicWorkbookAnswerConfirmed')
      )
    })
    return { ...validParams, type } as WorkbookListParams
  }
}
